import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import React from 'react';
import { InfinitySpinner } from './infinity-spinner';
import { type ThemeMode } from './theme-vars';
import tokens from './tokens.json';

declare module '@mui/material/styles' {
  /*
   * createTypography reads pxToRem from its input, but TypographyVariantsOptions omits it —
   * TypographyUtils is declared on the output type only. Augmenting is MUI's own mechanism for
   * this, and says the option is supported rather than casting past the gap.
   */
  interface TypographyVariantsOptions {
    pxToRem?: (px: number) => string;
  }

  interface PaletteColor {
    bg?: string;
  }
  interface SimplePaletteColorOptions {
    bg?: string;
  }
}

function getMuiPaletteMode(mode: ThemeMode): 'light' | 'dark' {
  let paletteMode: 'light' | 'dark' = 'light';
  if (mode === 'dark') {
    paletteMode = 'dark';
  }
  return paletteMode;
}

const buildThemeOptions = (mode: ThemeMode): ThemeOptions => ({
  palette: {
    mode: getMuiPaletteMode(mode),
    primary: {
      main: tokens.semantic[mode].primary,
      dark: tokens.colors.brand['700'],
      light: tokens.colors.brand['400'],
      bg: tokens.semantic[mode].primaryBg,
      contrastText: tokens.semantic[mode].onPrimary,
    },
    background: {
      default: tokens.semantic[mode].background,
      paper: tokens.semantic[mode].surface,
    },
    text: {
      primary: tokens.semantic[mode].text,
      secondary: tokens.semantic[mode].muted,
    },
    divider: tokens.semantic[mode].border,
    success: {
      main: tokens.semantic[mode].success,
      light: tokens.semantic[mode].successBg,
    },
    warning: {
      main: tokens.semantic[mode].warning,
      light: tokens.semantic[mode].warningBg,
    },
    info: {
      main: tokens.semantic[mode].info,
      light: tokens.semantic[mode].infoBg,
    },
    error: {
      main: tokens.semantic[mode].danger,
      light: tokens.semantic[mode].dangerBg,
    },
  },
  typography: {
    /*
     * MUI sizes every variant — and SvgIcon — through pxToRem, which resolves against the host
     * page's root font-size. These components are mounted into pages the design system does not
     * own, and the server-rendered ones declare a 14px root, so the identical component rendered
     * smaller there. These are the numbers a 16px root produces, so nothing moves where it is.
     */
    pxToRem: (size: number) => `${size}px`,
    fontFamily: tokens.typography.fontFamily.sans.join(', '),
    h1: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
    h2: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
    h3: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
    h4: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
    h5: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
    h6: { fontFamily: tokens.typography.fontFamily.serif.join(', ') },
  },
  shape: {
    borderRadius: Number.parseInt(tokens.radii.md, 10),
  },
  breakpoints: {
    values: tokens.breakpoints,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        label: 'SGS Input',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.semantic[mode].border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.semantic[mode].mutedSubtle,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.semantic[mode].primary,
          },
          // After the focus rule ON PURPOSE: an errored field stays red while focused, which the focus rule above would otherwise repaint
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.semantic[mode].danger,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.semantic[mode].danger,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          // :not(.MuiInputLabel-root) ON PURPOSE: InputLabel extends FormLabel, and a floating input label must keep its own size
          '&:not(.MuiInputLabel-root)': {
            fontSize: '16px',
            fontWeight: 600,
          },
          // The "/ ''" alt text keeps screen readers from announcing the star; the plain value is the fallback for browsers without it
          '&.Mui-required::before': {
            content: ['"*"', '"*" / ""'],
            marginRight: '0.25em',
          },
        },
        asterisk: {
          display: 'none',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '14px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiTypography: {
      // MUI 9 resolves the color prop through palette KEYS only ('error', 'textSecondary'), so a dotted token is inert without these
      variants: [
        { props: { color: 'text.primary' }, style: { color: tokens.semantic[mode].text } },
        { props: { color: 'text.secondary' }, style: { color: tokens.semantic[mode].muted } },
        { props: { color: 'text.disabled' }, style: { color: tokens.semantic[mode].mutedSubtle } },
        { props: { color: 'error.main' }, style: { color: tokens.semantic[mode].danger } },
        { props: { color: 'success.main' }, style: { color: tokens.semantic[mode].success } },
        { props: { color: 'warning.main' }, style: { color: tokens.semantic[mode].warning } },
        { props: { color: 'primary.main' }, style: { color: tokens.semantic[mode].primary } },
      ],
      styleOverrides: {
        h1: {
          fontSize: '30px',
          fontWeight: 600,
        },
        h2: {
          fontSize: '24px',
          fontWeight: 600,
        },
        h3: {
          fontSize: '20px',
          fontWeight: 600,
        },
        h4: {
          fontSize: '18px',
          fontWeight: 600,
        },
        h5: {
          fontSize: '16px',
          fontWeight: 600,
        },
        h6: {
          fontSize: '14px',
          fontWeight: 600,
        },
        subtitle1: {
          fontSize: '16px',
          fontWeight: 600,
        },
        subtitle2: {
          fontSize: '14px',
          fontWeight: 600,
        },
        caption: {
          fontSize: '12px',
          fontWeight: 600,
        },
        overline: {
          fontSize: '10px',
          fontWeight: 600,
        },
        button: {
          fontSize: '14px',
          fontWeight: 600,
        },
        body1: {
          fontSize: '16px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
        fullWidth: true,
        loadingIndicator: <InfinitySpinner size="default" color="inherit" />,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          height: '40px',
          textTransform: 'none',
        },
      },
    },
    MuiAlert: {
      variants: [
        {
          props: {
            severity: 'error',
          },
          style: {
            backgroundColor: tokens.semantic[mode].dangerBg,
            color: tokens.semantic[mode].danger,
          },
        },
        {
          props: {
            severity: 'warning',
          },
          style: {
            backgroundColor: tokens.semantic[mode].warningBg,
            color: tokens.semantic[mode].warning,
          },
        },
        {
          props: {
            severity: 'info',
          },
          style: {
            backgroundColor: tokens.semantic[mode].infoBg,
            color: tokens.semantic[mode].info,
          },
        },
        {
          props: {
            severity: 'success',
          },
          style: {
            backgroundColor: tokens.semantic[mode].successBg,
            color: tokens.semantic[mode].success,
          },
        },
      ],
    },
    MuiSelect: {
      defaultProps: {
        style: {
          borderRadius: Number.parseInt(tokens.radii.sm, 10),
          height: '48px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
      variants: [
        {
          props: {
            variant: 'outlined',
          },
          style: {
            backgroundColor: 'transparent',
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radii.sm,
        },
      },
    },
    MuiTab: {
      defaultProps: {
        sx: {
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 'auto',
          px: 2,
          color: 'text.secondary',
          '&.Mui-selected': {
            color: 'primary.main',
          },
          '&.Mui-disabled': {
            opacity: 0.7,
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
    },
  },
});

export const createMuiTheme = (mode: ThemeMode, overrides?: ThemeOptions) => {
  const baseOptions = buildThemeOptions(mode);
  return overrides ? createTheme(deepmerge(baseOptions, overrides)) : createTheme(baseOptions);
};
